import { apiClient } from "../index";

const MY_OVERVIEW_PATH = "/api/v1/dashboard/company-user/my-overview";
const TOKEN_ACTIVITY_PATH = "/api/v1/dashboard/company-user/token-activity";
const SOLO_PROGRESSION_PATH = "/api/v1/dashboard/company-user/solo-progression";
const CATEGORY_COVERAGE_PATH =
  "/api/v1/dashboard/company-user/category-coverage";
const MY_BOXES_PATH = "/api/v1/dashboard/company-user/my-boxes";
const TKSA_PROGRESS_PATH = "/api/v1/dashboard/company-user/tksa-progress";
const DIFFICULTY_STATS_PATH = "/api/v1/dashboard/company-user/difficulty-stats";
const TKSA_BREAKDOWN_PATH = "/api/v1/dashboard/company-user/tksa-breakdown";

/* ─────────────────────────────────────────────
   Section 1 — My Overview
   ───────────────────────────────────────────── */

export async function getMyOverview() {
  return apiClient.get(MY_OVERVIEW_PATH);
}

/**
 * Normalize the raw payload into the flat shape the five overview cards expect.
 * Defensive about missing / null fields so the UI never crashes on partial data.
 */
export function normalizeMyOverview(raw) {
  const comp = raw?.completion ?? {};
  const fts = raw?.first_try_success ?? {};
  const bc = raw?.boxes_created ?? {};
  const tok = raw?.tokens ?? {};
  const team = raw?.team ?? null;

  return {
    /* Boxes Completed card */
    boxesCompleted: comp.completed ?? 0,
    boxesAttempted: comp.attempted ?? 0,

    /* First-Try Success card */
    firstTrySuccess: fts.percentage ?? 0,

    /* Boxes Created card */
    boxesCreated: bc.total ?? 0,
    created: {
      approved: bc.approved ?? 0,
      pending: bc.pending ?? 0,
      rejected: bc.rejected ?? 0,
    },

    /* Tokens Remaining card */
    tokensUsedToday: tok.used_today ?? 0,
    dailyCap: tok.daily_cap ?? 0,
    tokensRemaining: tok.remaining ?? 0,

    /* My Team card — null when the user isn't on any team */
    team: team
      ? {
          id: team.team_id ?? null,
          name: team.team_name ?? "—",
          members: team.member_count ?? 0,
          active: team.active_challenge_count ?? 0,
        }
      : null,
  };
}

/* ─────────────────────────────────────────────
   Section 2 — Token Activity
   ───────────────────────────────────────────── */

export async function getTokenActivity() {
  return apiClient.get(TOKEN_ACTIVITY_PATH);
}

/**
 * Normalize token-activity into the shape the trend chart + stat tiles expect.
 * Formats ISO labels into "Mon D" for the x-axis.
 * Derives peakIdx from the series for the highlighted chart point.
 */
export function normalizeTokenActivity(raw) {
  const chart = raw?.usage_30d_chart ?? {};
  const labels = chart.labels ?? [];
  const series =
    chart.datasets?.find((d) => d.label === "Tokens")?.data ??
    chart.datasets?.[0]?.data ??
    [];

  const total = raw?.tokens_last_30_days ?? 0;
  const avg = raw?.daily_avg ?? 0;
  const today = raw?.tokens_today ?? 0;
  const conversations = raw?.total_conversations ?? 0;

  const peakVal = Math.max(...series, 0);
  const peakIdx = series.indexOf(peakVal);

  return {
    total,
    avg,
    today,
    conversations,
    peakIdx: peakIdx >= 0 ? peakIdx : series.length - 1,

    labels: labels.map((iso) => {
      const d = new Date(iso + "T00:00:00");
      return d.toLocaleDateString("en", { month: "short", day: "numeric" });
    }),
    series,
  };
}

/* ─────────────────────────────────────────────
   Section 3 — Solo Box Progression
   ───────────────────────────────────────────── */

export async function getSoloProgression() {
  return apiClient.get(SOLO_PROGRESSION_PATH);
}

/**
 * Normalize solo-progression into:
 *  • chart labels (formatted) + two cumulative series (attempted, completed)
 *  • completion overview scalars + derived percentage
 */
export function normalizeSoloProgression(raw) {
  const chart = raw?.attempt_history_7d_chart ?? {};
  const labels = chart.labels ?? [];
  const datasets = chart.datasets ?? [];

  const attemptedDaily =
    datasets.find((d) => d.label === "Attempted")?.data ??
    datasets[0]?.data ??
    [];
  const completedDaily =
    datasets.find((d) => d.label === "Completed")?.data ??
    datasets[1]?.data ??
    [];

  /* Accumulate daily values into running totals for the cumulative line */
  const cumulate = (arr) => {
    let sum = 0;
    return arr.map((v) => (sum += v));
  };

  const co = raw?.completion_overview ?? {};
  const completed = co.completed ?? 0;
  const attempted = co.attempted ?? 0;
  const untouched = co.untouched ?? 0;
  const total = completed + attempted + untouched;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  return {
    labels: labels.map((iso) => {
      const d = new Date(iso + "T00:00:00");
      return d.toLocaleDateString("en", { month: "short", day: "numeric" });
    }),
    attemptedCum: cumulate(attemptedDaily),
    completedCum: cumulate(completedDaily),

    completion: {
      completed,
      attempted,
      untouched,
      pct,
    },
  };
}

/* ─────────────────────────────────────────────
   Section 4 — Category Coverage & Recommendations
   ───────────────────────────────────────────── */

export async function getCategoryCoverage() {
  return apiClient.get(CATEGORY_COVERAGE_PATH);
}

/**
 * Extract a short display name from the long "Name - Description …" labels
 * the API returns.  Falls back to the full string if no dash is found.
 */
function shortName(full = "") {
  const idx = full.indexOf(" - ");
  return idx > 0 ? full.slice(0, idx).trim() : full;
}

/**
 * Normalize category-coverage into:
 *  • chart labels + values (sorted desc by completion %)
 *  • per-category detail rows
 *  • mastered / untouched name lists (short names)
 */
export function normalizeCategoryCoverage(raw) {
  const chart = raw?.completion_by_category_chart ?? {};
  const labels = chart.labels ?? [];
  const values =
    chart.datasets?.find((d) => d.label === "Completion %")?.data ??
    chart.datasets?.[0]?.data ??
    [];

  const categories = (raw?.categories ?? []).map((c) => ({
    id: c.category_id,
    name: shortName(c.name),
    fullName: c.name ?? "",
    total: c.total ?? 0,
    completed: c.completed ?? 0,
    pct: c.completion_pct ?? 0,
  }));

  return {
    chart: {
      labels: labels.map(shortName),
      values,
    },
    categories,
    mastered: (raw?.fully_mastered ?? []).map(shortName),
    untouched: (raw?.untouched ?? []).map(shortName),
  };
}

/* ─────────────────────────────────────────────
   Section 5 — My Boxes
   ───────────────────────────────────────────── */

export async function getMyBoxes(limit = 20, offset = 0) {
  return apiClient.get(`${MY_BOXES_PATH}?limit=${limit}&offset=${offset}`);
}

/** Turn an ISO timestamp into a compact relative label: "2h ago", "3 days ago", etc. */
function timeAgo(iso) {
  if (!iso) return "—";
  const now = Date.now();
  const then = new Date(iso).getTime();
  const sec = Math.max(Math.floor((now - then) / 1000), 0);

  if (sec < 60) return "Just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min${min === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? "" : "s"} ago`;
}

/**
 * Normalize my-boxes into:
 *  • counts for the donut (total / approved / pending / rejected)
 *  • boxes array with derived status and relative time label
 */
export function normalizeMyBoxes(raw) {
  const c = raw?.counts ?? {};
  const boxes = (raw?.boxes ?? []).map((b) => ({
    id: b.id,
    name: b.name ?? "Untitled",
    status: b.status ?? "pending",
    time: timeAgo(b.created_at),
  }));

  return {
    counts: {
      total: c.total ?? 0,
      approved: c.approved ?? 0,
      pending: c.pending ?? 0,
      rejected: c.rejected ?? 0,
    },
    boxes,
  };
}

/* ─────────────────────────────────────────────
   Section 6 — TKSA Progress
   ───────────────────────────────────────────── */

export async function getTksaProgress(framework = "scywf_v1") {
  return apiClient.get(
    `${TKSA_PROGRESS_PATH}?framework=${encodeURIComponent(framework)}`,
  );
}

/**
 * Normalize tksa-progress into the shape the dashboard section expects.
 * Each TKSA bucket gets its own covered / total / pct triple.
 */
export function normalizeTksaProgress(raw) {
  const byType = raw?.by_type ?? {};

  const bucket = (key) => {
    const b = byType[key] ?? {};
    const covered = b.covered ?? 0;
    const total = b.total ?? 0;
    const pct = total > 0 ? Math.round((covered / total) * 100) : 0;
    return { covered, total, pct };
  };

  return {
    frameworkCode: raw?.framework_code ?? "",
    frameworkName: raw?.framework_name ?? "Unknown Framework",
    itemsCovered: raw?.items_covered ?? 0,
    itemsTotal: raw?.items_total ?? 0,
    coveragePct: raw?.coverage_pct ?? 0,
    task: bucket("TASK"),
    knowledge: bucket("KNOWLEDGE"),
    skill: bucket("SKILL"),
    ability: bucket("ABILITY"),
  };
}

/* ─────────────────────────────────────────────
   Section 7 — Difficulty Stats
   ───────────────────────────────────────────── */

export async function getDifficultyStats() {
  return apiClient.get(DIFFICULTY_STATS_PATH);
}

/** Format minutes into a compact human label: "2.6 min", "1h 12m", "—" */
function fmtMinutes(m) {
  if (!m || m <= 0) return "—";
  if (m < 60) return `${Math.round(m * 10) / 10} min`;
  const h = Math.floor(m / 60);
  const rem = Math.round(m % 60);
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
}

/**
 * Normalize difficulty-stats into an ordered [easy, medium, hard] array
 * with derived display values.
 */
export function normalizeDifficultyStats(raw) {
  const buckets = raw?.buckets ?? [];

  const ORDER = ["easy", "medium", "hard"];
  const mapped = ORDER.map((diff) => {
    const b = buckets.find((x) => x.difficulty === diff) ?? {};
    return {
      difficulty: diff,
      completed: b.completed ?? 0,
      avgMinutes: b.avg_completion_minutes ?? 0,
      avgLabel: fmtMinutes(b.avg_completion_minutes),
    };
  });

  const totalCompleted = mapped.reduce((s, b) => s + b.completed, 0);

  return { buckets: mapped, totalCompleted };
}

/* ─────────────────────────────────────────────
   Section 6.2 — TKSA Breakdown
   ───────────────────────────────────────────── */

export async function getTksaBreakdown(framework = "scywf_v1") {
  return apiClient.get(
    `${TKSA_BREAKDOWN_PATH}?framework=${encodeURIComponent(framework)}`,
  );
}

/** Normalize a single by_type block into a consistent shape. */
function normalizeTksaBuckets(byType = {}) {
  const bucket = (key) => {
    const b = byType[key] ?? {};
    const covered = b.covered ?? 0;
    const total = b.total ?? 0;
    return {
      covered,
      total,
      pct: total > 0 ? Math.round((covered / total) * 100) : 0,
    };
  };
  return {
    task: bucket("TASK"),
    knowledge: bucket("KNOWLEDGE"),
    skill: bucket("SKILL"),
    ability: bucket("ABILITY"),
  };
}

/** Normalize a coverage block (items_covered, items_total, coverage_pct, by_type). */
function normalizeCoverageBlock(cov = {}) {
  return {
    itemsCovered: cov.items_covered ?? 0,
    itemsTotal: cov.items_total ?? 0,
    coveragePct: cov.coverage_pct ?? 0,
    byType: normalizeTksaBuckets(cov.by_type),
  };
}

/**
 * Normalize tksa-breakdown into:
 *  • framework-level coverage
 *  • groups[] each with coverage + work_roles[]
 *  • groups grouped by parent_category for optional accordion headers
 */
export function normalizeTksaBreakdown(raw) {
  const fc = raw?.framework_coverage ?? {};

  const groups = (raw?.groups ?? []).map((g) => ({
    code: g.group_code ?? "",
    name: g.group_name ?? "Unknown",
    type: g.group_type ?? "specialty",
    parentCode: g.parent_category_code ?? "",
    parentName: g.parent_category_name ?? "",
    coverage: normalizeCoverageBlock(g.coverage),
    workRoles: (g.work_roles ?? []).map((wr) => ({
      code: wr.code ?? "",
      name: wr.name ?? "Unknown",
      coverage: normalizeCoverageBlock(wr.coverage),
    })),
  }));

  return {
    frameworkCode: raw?.framework_code ?? "",
    frameworkName: raw?.framework_name ?? "Unknown Framework",
    groupType: raw?.group_type ?? "specialty",
    frameworkCoverage: normalizeCoverageBlock(fc),
    groups,
  };
}
